import { useEffect } from 'react';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import {
	useIsFirstActivationDateAfterOneDayMemo,
	useIsFirstActivationDateWithinLast30Days,
} from '@atlassian/jira-onboarding-core';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { FREE_EDITION } from '@atlassian/jira-shared-types';
import {
	useAccountId,
	useAppEditions,
	useCloudId,
	useIsSiteAdmin,
} from '@atlassian/jira-tenant-context-controller';
import {
	getIsRenotifyMessageDismissed,
	getTimeOfLastResendInvitation,
	hasAtLeast24HoursElapsed,
	type DismissTraitRequestProp,
} from './utils';

export const useRenotifyInviteesExperimentForJSW = () => {
	const { data } = useProjectContext();
	const { software: softwareEdition } = useAppEditions();

	const isSiteAdmin = useIsSiteAdmin();
	const isSoftwareProject = data?.projectType === SOFTWARE_PROJECT;
	const hasFreeSoftwareEdition = softwareEdition === FREE_EDITION;
	const isWithin30Day = useIsFirstActivationDateWithinLast30Days();
	const isAfterOneDay = useIsFirstActivationDateAfterOneDayMemo();

	const cloudId = useCloudId();
	const userId = useAccountId();

	const {
		loading: isResendTraitFetching,
		error: resendTraitError,
		data: timeOfLastResendInvitation,
		fetch: fetchResendTrait,
	} = useService<number, string>(getTimeOfLastResendInvitation);

	const {
		loading: isDismissTraitFetching,
		error: dismissTraitError,
		data: isRenotifyMessageDismissed,
		fetch: fetchDismissTrait,
	} = useService<boolean, DismissTraitRequestProp>(getIsRenotifyMessageDismissed);

	const isBasicTarget =
		isSiteAdmin && isSoftwareProject && hasFreeSoftwareEdition && isAfterOneDay && isWithin30Day;
	const isTargeted =
		isBasicTarget &&
		!isRenotifyMessageDismissed &&
		timeOfLastResendInvitation !== undefined &&
		hasAtLeast24HoursElapsed(timeOfLastResendInvitation);

	useEffect(() => {
		if (!isBasicTarget) {
			return;
		}
		fetchResendTrait(cloudId);
		if (userId) {
			fetchDismissTrait({ cloudId, userId });
		}
	}, [isBasicTarget, userId, cloudId, fetchResendTrait, fetchDismissTrait]);

	useEffect(() => {
		if (resendTraitError) {
			fireErrorAnalytics({
				error: resendTraitError,
				meta: {
					id: 'getTimeOfLastResendInvitation',
					packageName: 'jiraExperiments',
				},
			});
		}
		if (dismissTraitError) {
			fireErrorAnalytics({
				error: dismissTraitError,
				meta: {
					id: 'getIsRenotifyMessageDismissed',
					packageName: 'jiraExperiments',
				},
			});
		}
	}, [resendTraitError, dismissTraitError]);

	return {
		isLoading: isResendTraitFetching || isDismissTraitFetching,
		isJswTargeted: isTargeted,
		onFetchedPendingUsers: () =>
			expValEquals('jsw_remind_admins_about_invited_users', 'isEnabled', true),
	};
};
